import { useQuery } from '@tanstack/react-query';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
} from 'chart.js';
import { FC, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import PulsingLoader, { LoaderHolder } from '@/components/PulsingLoader';
import { Table, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

const Dialog = styled(StyledDialog)`
  width: 464px;
  text-align: left;
  padding: 0;
  width: 1000px;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
`;

const Inner = styled.div`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const Footer = styled.div`
  padding: 24px;
  display: flex;
  justify-content: flex-end;
`;

const StyledTable = styled(Table)`
  box-shadow: 0px -1px 0px 0px #e9e9e9 inset;

  th,
  td {
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
      width: 25%;
    }
    &:last-child {
      padding-right: 24px;
      width: 50%;
    }
  }

  td {
    font-size: 12px;
    font-weight: 700;
  }
`;

const Thumb = styled.img`
  margin-right: 6px;
  width: 16px;
  height: 16px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
`;

const PageTitle = styled.div`
  display: flex;
  align-items: center;
`;

const Graph = styled.div`
  width: 100%;
`;

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const graphOptions = {
  responsive: true,
  layout: {
    padding: {
      top: 5,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
        borderWidth: 1,
        borderColor: '#E9E9E9',
      },
      ticks: {
        font: {
          size: 9,
          weight: '700',
          family: 'Manrope',
        },
        callback: (value: any, index: number) => {
          return (index + 1).toString().padStart(2, '0');
        },
      },
    },
    y: {
      min: 0,
      ticks: {
        font: {
          size: 9,
          weight: '700',
          family: 'Manrope',
        },
      },
      grid: {
        lineWidth: 0.5,
        color: '#E9E9E9',
        drawTicks: false,
        drawBorder: false,
      },
    },
  },
};

const PerformanceModal: FC<Props> = ({ activePage, onClose }) => {
  const { pageId } = useParams();
  const theme = useTheme();

  const [network] = useNetwork();

  const { data: collection, isLoading } = useQuery(
    ['editorial_distribution_queue_performance', { pageId, network }],
    () =>
      api.get<
        never,
        {
          [hours: string]: number;
        }
      >('/v1/editorial/queue/performance', {
        params: {
          channel_id: pageId,
          network,
        },
      }),
    {
      staleTime: 60_000,
    },
  );

  const { labels, data, max } = useMemo(() => {
    if (!collection) {
      return {};
    }

    const labels = Object.keys(collection);
    const data = Object.values(collection);
    const max = data.sort((a, b) => b - a)[0];

    return { labels, data, max };
  }, [collection]);

  const graphData = {
    labels,
    datasets: [
      {
        data,
        borderColor: theme.colors['purple'],
        borderWidth: 2,
        backgroundColor: 'rgba(65, 54, 207, 0.1)', //'#EEEDFB',
        tension: 0.4,
        pointRadius: 0,
        fill: 'origin',
      },
    ],
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">
            Page Performance
            <Text as="span" $type="button" $color="gray">
              {' '}
              · Last Month
            </Text>
          </Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Inner>
          {isLoading ? (
            <LoaderHolder>
              <PulsingLoader />
              <Text $color="gray" $type="captionSemibold">
                Loading...
              </Text>
            </LoaderHolder>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <Th>Page Name</Th>
                  <Th>
                    Site Visits Performance
                    <br />
                    <Text as="span" $color="gray" $type="extraSmall">
                      Top Median
                    </Text>
                  </Th>
                  <Th>
                    Site Visits Performance
                    <br />
                    <Text as="span" $color="gray" $type="extraSmall">
                      Median by Hour
                    </Text>
                  </Th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Td>
                    <PageTitle>
                      {!!activePage.thumbnail && (
                        <Thumb src={activePage.thumbnail} alt="" />
                      )}
                      {activePage.name}
                    </PageTitle>
                  </Td>
                  <Td>{max}</Td>
                  <Td>
                    <Graph>
                      <Line data={graphData} options={graphOptions} />
                    </Graph>
                  </Td>
                </tr>
              </tbody>
            </StyledTable>
          )}
        </Inner>
        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default PerformanceModal;
